import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { LayersList } from '@deck.gl/core';
import { MapboxOverlay, MapboxOverlayProps } from '@deck.gl/mapbox';
import { IconLayer, ScatterplotLayer } from '@deck.gl/layers';
import Map, {
  MapRef,
  NavigationControl,
  ScaleControl,
  useControl,
} from 'react-map-gl';
import 'mapbox-gl/dist/mapbox-gl.css';
import { layers as availableLayers } from '../enums';
import { HISTORICAL_CENIA_ATTRIBUTION } from '../layers';
import { MapControls } from '../MapControls';
import { useLayer } from '../LayerContext/useLayer';
import {
  BASE_MAPBOX_STYLE_URL,
  DEFAULT_INITIAL_COORDS,
  MAPBOX_ACCESS_TOKEN,
} from '../../consts';
import { getMaterialColor } from '../../helpers';
import {
  createVisualizationFillIconUrl,
  createVisualizationOutlineIconUrl,
  getVisualizationStyleKey,
} from './shapeSvg';
import { createSyntheticPoints } from './syntheticPoints';
import { getMostSpecificDefinition } from './matching';
import {
  getVisualizationMarkerSizes,
  getVisualizationShapeSizeMultiplier,
} from './markerSize';
import {
  MapBounds,
  VisualizationDefinition,
  VisualizationFilterMetadata,
  VisualizationStyle,
  VisualizerFind,
} from './types';

interface SitePosition {
  id: string;
  latitude?: number | null;
  longitude?: number | null;
}

interface StyledPoint extends VisualizerFind {
  style: VisualizationStyle;
  custom: boolean;
}

interface VisualizerMapViewProps {
  site: SitePosition | null;
  finds: VisualizerFind[];
  definitions: VisualizationDefinition[];
  metadata: VisualizationFilterMetadata;
  hideOthers: boolean;
}

function DeckGLOverlay(props: MapboxOverlayProps) {
  const overlay = useControl<MapboxOverlay>(() => new MapboxOverlay(props));
  overlay.setProps(props);

  return null;
}

const DEFAULT_BOUNDS_DELTA = 0.04;
const VISUALIZER_LAYERS = Object.keys(availableLayers).filter(
  (layer) => layer !== 'copernicus'
);
// loaders.gl cannot decode SVG files as ImageBitmap objects. IconLayer must
// load these generated SVG data URLs as browser images before atlas packing.
const VISUALIZATION_ICON_LOAD_OPTIONS = {
  image: { type: 'image' as const },
};
const DEFAULT_POINT_STYLE: Omit<VisualizationStyle, 'primaryColor'> = {
  shape: 'circle',
  sizeScale: 1,
  outlined: true,
  secondaryColor: null,
};
const mapContainerStyles = {
  position: 'relative' as const,
  width: '100%',
  height: '100%',
  minHeight: 'inherit',
};
const mapElementStyles = { width: '100%', height: '100%' };

function getDefaultBounds(longitude: number, latitude: number): MapBounds {
  return {
    west: longitude - DEFAULT_BOUNDS_DELTA,
    east: longitude + DEFAULT_BOUNDS_DELTA,
    south: latitude - DEFAULT_BOUNDS_DELTA,
    north: latitude + DEFAULT_BOUNDS_DELTA,
  };
}

function toCssColor(color: [number, number, number]): string {
  return `rgb(${color.join(',')})`;
}

function isInsideBounds(find: VisualizerFind, bounds: MapBounds): boolean {
  return (
    find.longitude >= bounds.west &&
    find.longitude <= bounds.east &&
    find.latitude >= bounds.south &&
    find.latitude <= bounds.north
  );
}

export const VisualizerMapView = React.memo(
  ({
    site,
    finds,
    definitions,
    metadata,
    hideOthers,
  }: VisualizerMapViewProps) => {
    const { layer } = useLayer();
    const containerRef = useRef<HTMLDivElement>(null);
    const mapRef = useRef<MapRef>(null);
    const center = {
      longitude: site?.longitude ?? DEFAULT_INITIAL_COORDS.longitude,
      latitude: site?.latitude ?? DEFAULT_INITIAL_COORDS.latitude,
    };
    const initialViewStateRef = useRef({
      longitude: center.longitude,
      latitude: center.latitude,
      zoom: site ? 15 : DEFAULT_INITIAL_COORDS.zoom,
    });
    const [bounds, setBounds] = useState<MapBounds>(() =>
      getDefaultBounds(center.longitude, center.latitude)
    );
    const [viewMetrics, setViewMetrics] = useState({
      zoom: initialViewStateRef.current.zoom,
      latitude: initialViewStateRef.current.latitude,
    });

    const updateBounds = useCallback((map) => {
      const next = map.getBounds();
      setBounds({
        west: next.getWest(),
        south: next.getSouth(),
        east: next.getEast(),
        north: next.getNorth(),
      });
    }, []);

    const resizeMap = useCallback(() => {
      const map = mapRef.current;
      if (!map) {
        return;
      }

      map.resize();
      updateBounds(map);
    }, [updateBounds]);

    useEffect(() => {
      const container = containerRef.current;
      if (!container) {
        return;
      }

      let secondFrame: number | null = null;
      const firstFrame = window.requestAnimationFrame(() => {
        resizeMap();
        secondFrame = window.requestAnimationFrame(resizeMap);
      });
      const observer = new ResizeObserver(resizeMap);
      observer.observe(container);

      return () => {
        window.cancelAnimationFrame(firstFrame);
        if (secondFrame != null) {
          window.cancelAnimationFrame(secondFrame);
        }
        observer.disconnect();
      };
    }, [resizeMap]);

    useEffect(() => {
      const longitude = site?.longitude ?? DEFAULT_INITIAL_COORDS.longitude;
      const latitude = site?.latitude ?? DEFAULT_INITIAL_COORDS.latitude;

      setBounds(getDefaultBounds(longitude, latitude));
      setViewMetrics({
        zoom: site ? 15 : DEFAULT_INITIAL_COORDS.zoom,
        latitude,
      });
      mapRef.current?.jumpTo({
        center: [longitude, latitude],
        zoom: site ? 15 : DEFAULT_INITIAL_COORDS.zoom,
      });
    }, [site]);

    const handleMapLoad = useCallback((event) => {
      resizeMap();
      updateBounds(event.target);
    }, [resizeMap, updateBounds]);

    const handleMoveEnd = useCallback((event) => {
      updateBounds(event.target);
    }, [updateBounds]);

    const handleZoom = useCallback((event) => {
      setViewMetrics({
        zoom: event.viewState.zoom,
        latitude: event.viewState.latitude,
      });
    }, []);

    const activeLayers = useMemo(() => {
      if (!availableLayers[layer]) {
        return [];
      }

      const freshInstances = (
        availableLayers[layer].constructors || []
      ).map((constructor) => constructor());

      return [
        ...(availableLayers[layer].instances || []),
        ...freshInstances,
      ];
    }, [layer]);

    const styledPoints = useMemo<StyledPoint[]>(() => {
      const visibleRepresentativeByDefinition = new globalThis.Map<
        string,
        VisualizerFind
      >();
      const actualPoints = finds
        .map((find): StyledPoint | null => {
          const definition = getMostSpecificDefinition(
            find,
            definitions,
            metadata
          );

          if (definition) {
            if (isInsideBounds(find, bounds)) {
              visibleRepresentativeByDefinition.set(definition.id, find);
            }

            return {
              ...find,
              style: definition.style,
              custom: true,
            };
          }

          if (hideOthers) {
            return null;
          }

          return {
            ...find,
            style: {
              ...DEFAULT_POINT_STYLE,
              primaryColor: toCssColor(
                getMaterialColor(find.material || '', find.draft)
              ),
            },
            custom: false,
          };
        })
        .filter((point): point is StyledPoint => point != null);

      const synthetic = definitions.flatMap((definition) => {
        const representative = visibleRepresentativeByDefinition.get(
          definition.id
        );

        return createSyntheticPoints(
          definition,
          representative ? [representative] : [],
          metadata,
          bounds
        ).map(
          (point): StyledPoint => ({
            ...point,
            style: definition.style,
            custom: true,
          })
        );
      });

      return [...actualPoints, ...synthetic];
    }, [bounds, definitions, finds, hideOthers, metadata]);

    const defaultPoints = useMemo(
      () => styledPoints.filter((point) => !point.custom),
      [styledPoints]
    );
    const customPoints = useMemo(
      () => styledPoints.filter((point) => point.custom),
      [styledPoints]
    );
    const outlinedCustomPoints = useMemo(
      () => customPoints.filter((point) => point.style.outlined),
      [customPoints]
    );

    const defaultPointLayer = useMemo(
      () =>
        new ScatterplotLayer<StyledPoint>({
          id: 'visualizer-default-find-points',
          data: defaultPoints,
          opacity: 0.75,
          radiusScale: 1,
          radiusMinPixels: 2,
          getPosition: (point) => [point.longitude, point.latitude],
          getFillColor: (point) =>
            getMaterialColor(point.material || '', point.draft),
          getRadius: 3,
          radiusUnits: 'pixels',
          stroked: true,
          lineWidthMaxPixels: 2,
          pickable: false,
        }),
      [defaultPoints]
    );

    const outlinePointLayer = useMemo(
      () =>
        new IconLayer<StyledPoint>({
          id: 'visualizer-find-point-outlines',
          data: outlinedCustomPoints,
          getPosition: (point) => [point.longitude, point.latitude],
          getIcon: (point) => ({
            id: `outline:${point.style.shape}`,
            url: createVisualizationOutlineIconUrl(point.style.shape),
            width: 96,
            height: 96,
            mask: false,
          }),
          getSize: (point) =>
            getVisualizationMarkerSizes(
              point.style.sizeScale *
                getVisualizationShapeSizeMultiplier(point.style.shape),
              true,
              viewMetrics.zoom,
              viewMetrics.latitude
            ).outline || 0,
          sizeUnits: 'pixels',
          loadOptions: VISUALIZATION_ICON_LOAD_OPTIONS,
          pickable: false,
          updateTriggers: {
            getIcon: outlinedCustomPoints.map((point) => point.style.shape),
            getSize: [
              viewMetrics.zoom,
              viewMetrics.latitude,
              ...outlinedCustomPoints.map((point) => point.style.sizeScale),
            ],
          },
        }),
      [outlinedCustomPoints, viewMetrics]
    );

    const pointLayer = useMemo(
      () =>
        new IconLayer<StyledPoint>({
          id: 'visualizer-find-point-fills',
          data: customPoints,
          getPosition: (point) => [point.longitude, point.latitude],
          getIcon: (point) => ({
            id: `fill:${getVisualizationStyleKey(point.style)}`,
            url: createVisualizationFillIconUrl(point.style),
            width: 96,
            height: 96,
            mask: false,
          }),
          getSize: (point) =>
            getVisualizationMarkerSizes(
              point.style.sizeScale *
                getVisualizationShapeSizeMultiplier(point.style.shape),
              point.style.outlined,
              viewMetrics.zoom,
              viewMetrics.latitude
            ).fill,
          sizeUnits: 'pixels',
          loadOptions: VISUALIZATION_ICON_LOAD_OPTIONS,
          pickable: false,
          updateTriggers: {
            getIcon: customPoints.map((point) =>
              getVisualizationStyleKey(point.style)
            ),
            getSize: [
              viewMetrics.zoom,
              viewMetrics.latitude,
              ...customPoints.map((point) => [
                point.style.sizeScale,
                point.style.outlined,
              ]),
            ],
          },
        }),
      [customPoints, viewMetrics]
    );

    const layers: LayersList = useMemo(
      () => [
        ...activeLayers,
        defaultPointLayer,
        outlinePointLayer,
        pointLayer,
      ],
      [activeLayers, defaultPointLayer, outlinePointLayer, pointLayer]
    );

    return (
      <div ref={containerRef} style={mapContainerStyles}>
        <Map
          ref={mapRef}
          initialViewState={initialViewStateRef.current}
          mapStyle={BASE_MAPBOX_STYLE_URL}
          mapboxAccessToken={MAPBOX_ACCESS_TOKEN}
          customAttribution={
            layer === 'aerialCenia1950s'
              ? HISTORICAL_CENIA_ATTRIBUTION
              : undefined
          }
          onLoad={handleMapLoad}
          onMoveEnd={handleMoveEnd}
          onZoom={handleZoom}
          style={mapElementStyles}
        >
          <DeckGLOverlay layers={layers} />
          <MapControls
            allowedLayers={VISUALIZER_LAYERS}
            premiumLayersAvailable
            neutralStyle
            showNullLayer
          />
          <ScaleControl position="bottom-left" />
          <NavigationControl position="bottom-left" showCompass={false} />
        </Map>
      </div>
    );
  }
);

export * from './matching';
export * from './markerSize';
export * from './normalizeFind';
export * from './shapeSvg';
export * from './syntheticPoints';
export * from './types';
export * from './VisualizationSymbol';
